import React, { useState, useMemo, useEffect } from 'react';
import { Check, Minus } from 'lucide-react';
import { sharepointTabConfig } from "../../constants/sharepointTabConfig";
import { ExcludedItems, SharePointTabProps, DEFAULT_PAGINATION } from '../../types/sharepoint.types';
import SharePointItemsView from './SharePointItemsView';

const ITEMS_PER_PAGE = 10;

const SharePointTab: React.FC<SharePointTabProps> = ({
  activeSubTab,
  displayName,
  matchSubTab,
  visibility,
  items,
  itemCount,
  handleTabChange,
  handleCheckboxChange,
  checkedItems,
  sharepointId,
  excludedItems,
  updateExcludedItems,
  selectedItems,
  drillDownState,
  onItemClick,
  onBackToLists,
  onNavigateNext,
  onNavigatePrevious,
  onPageSizeChange,
  onDocumentExclusion,
  onLibraryExclusion,
  isDocumentExcluded,
  getLibrarySelectionState,
  sharepoint,
  searchTerm // NEW: Accept searchTerm as prop instead of managing it locally
}) => {
  // REMOVED: Local searchTerm state since it's now managed in SharePointPreviewTab
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // MOVED: All hooks must be called before any early returns
  useEffect(() => {
    // REMOVED: setSearchTerm("") since it's no longer local state
    setCurrentPage(1);
  }, [activeSubTab, drillDownState?.isViewingItems]);

  // NEW: Reset to first page when searchTerm changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const tabConfig = sharepointTabConfig[activeSubTab];
  const { label, key, idField, nameField } = tabConfig || {};

  const filteredItems = useMemo(() => {
    if (!nameField || !items) return items || [];
    
    // Use the searchTerm prop instead of local state
    return items.filter((item) => {
      const itemName = nameField.split('.').reduce((obj, key) => obj?.[key], item);
      return itemName?.toLowerCase().includes((searchTerm || "").toLowerCase());
    });
  }, [items, searchTerm, nameField]); // Updated dependency

  // NOW safe to do early returns after all hooks
  if (!items || items.length === 0) {
    return (
      <div className="flex items-center justify-center h-40">
        <div className="text-center text-gray-500">
          <h3 className="text-lg font-semibold mb-2">No {displayName} Available</h3>
          <p className="text-sm">This may be due to URL-specific targeting or no data in this category.</p>
        </div>
      </div>
    );
  }

  console.log('📋 SharePointTab rendered with:', {
    sharepointId,
    excludedLibraries: excludedItems?.excluded_document_libraries || []
  });

  const formatDate = (dateString: string): string => {
    if (!dateString) return 'N/A';
    try {
      const cleanDateString = dateString.replace(/ AM$/i, '').replace(/ PM$/i, '');
      const date = new Date(cleanDateString);

      if (isNaN(date.getTime())) {
        return 'Invalid Date';
      }

      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = date.getFullYear();

      return `${month}/${day}/${year}`;
    } catch (error) {
      return 'N/A';
    }
  };

  const getCreatedBy = (item: any): string => {
    if (item.created_by?.name) {
      return item.created_by.name;
    }
    return 'N/A';
  };

  // Helper function to render the display title with safe checks
  const renderItemTitle = (item: any, itemName: string) => {
    // SAFE CHECKS: Only show breadcrumb for document libraries with folder targeting
    const isDocumentLibrary = activeSubTab === 'document_libraries';
    const hasUrlAnalysis = sharepoint?.url_analysis;
    const isFolderTargeted = hasUrlAnalysis?.url_type === 'folder';
    const hasFolderPath = hasUrlAnalysis?.target_folder_path;
    
    // Show breadcrumb only if ALL conditions are met
    if (isDocumentLibrary && isFolderTargeted && hasFolderPath) {
      return (
        <div className="flex items-center gap-2">
          <span>{itemName}</span>
          <span className="text-gray-400 text-sm">›</span>
          <span className="text-blue-600 font-medium">{hasUrlAnalysis.target_folder_path}</span>
        </div>
      );
    }
    
    // Default: just show the item name (for lists, non-targeted libraries, etc.)
    return itemName;
  };

  const renderLibraryCheckbox = (item: any, itemId: string) => {
    console.log('🔍 renderLibraryCheckbox conditions:', {
      activeSubTab,
      isDocumentLibraries: activeSubTab === 'document_libraries',
      hasGetLibrarySelectionState: !!getLibrarySelectionState,
      hasOnLibraryExclusion: !!onLibraryExclusion,
      allConditionsMet: activeSubTab === 'document_libraries' && getLibrarySelectionState && onLibraryExclusion
    });

    if (activeSubTab === 'document_libraries' && getLibrarySelectionState && onLibraryExclusion) {
      console.log('✅ Using LIBRARY checkbox logic');
      const sharepoint = { excludedItems } as any;
      const selectionState = getLibrarySelectionState(itemId, sharepoint);
      
      return (
        <div
          className={`h-6 w-6 border-2 border-gray-300 rounded-full flex items-center justify-center cursor-pointer ${
            selectionState === 'checked' 
              ? 'bg-black border-black' 
              : selectionState === 'indeterminate'
                ? 'bg-gray-400 border-gray-400'
                : 'bg-white'
          }`}
          onClick={(e) => {
            console.log('🔴 LIBRARY CHECKBOX CLICKED!', itemId, selectionState);
            e.stopPropagation();
            handleLibraryCheckboxChange(itemId, selectionState);
          }}
          id={`checkbox-${itemId}`}
        >
          {selectionState === 'checked' && <Check className="h-4 w-4 text-white" />}
          {selectionState === 'indeterminate' && <Minus className="h-4 w-4 text-white" />}
        </div>
      );
    } else {
      console.log('📄 Using regular checkbox logic');
      return (
        <div
          className={`h-6 w-6 border-2 border-gray-300 rounded-full flex items-center justify-center cursor-pointer ${
            checkedItems.includes(itemId) ? 'bg-black' : 'bg-white'
          }`}
          onClick={(e) => {
            e.stopPropagation();
            handleItemCheckboxChange(itemId, key);
          }}
          id={`checkbox-${itemId}`}
        >
          {checkedItems.includes(itemId) && <Check className="h-4 w-4 text-white" />}
        </div>
      );
    }
  };

  const handleLibraryCheckboxChange = (libraryId: string, currentState: 'checked' | 'unchecked' | 'indeterminate') => {
    console.log('🔴 LIBRARY CHECKBOX CLICKED!', {
      libraryId, 
      currentState,
      excludedItemsBefore: excludedItems.excluded_document_libraries
    });
    
    if (!onLibraryExclusion) {
      handleItemCheckboxChange(libraryId, key);
      return;
    }

    // THREE-STATE LOGIC:
    switch (currentState) {
      case 'checked':
        // Currently all documents are included → Exclude entire library
        console.log('📋 CHECKED → UNCHECKED: Excluding entire library');
        onLibraryExclusion(sharepointId, libraryId, true);
        break;
        
      case 'unchecked':
        // Currently entire library is excluded → Include entire library
        console.log('📋 UNCHECKED → CHECKED: Including entire library');
        onLibraryExclusion(sharepointId, libraryId, false);
        break;
        
      case 'indeterminate':
        // Currently some documents excluded → Exclude entire library (most intuitive)
        console.log('📋 INDETERMINATE → UNCHECKED: Excluding entire library');
        onLibraryExclusion(sharepointId, libraryId, true);
        break;
        
      default:
        console.warn('⚠️ Unknown checkbox state:', currentState);
        break;
    }
    
    // Only call for non-document-library items (to avoid conflicts)
    if (activeSubTab !== 'document_libraries') {
      handleItemCheckboxChange(libraryId, key);
    }
  };

  if (!tabConfig || displayName !== matchSubTab) {
    if (!tabConfig) {
      console.error('Invalid activeSubTab:', activeSubTab);
    }
    return null;
  }

  if (drillDownState?.isViewingItems && onBackToLists) {
    console.log('📤 SharePointTab passing onLibraryExclusion:', !!onLibraryExclusion);

    return (
      <SharePointItemsView
        items={drillDownState.items}
        isLoading={drillDownState.isLoading}
        error={drillDownState.error}
        selectedItem={drillDownState.selectedItem}
        itemType={drillDownState.itemType}
        onBack={onBackToLists}
        pagination={drillDownState.pagination || DEFAULT_PAGINATION}
        isLoadingPage={drillDownState.isLoadingPage || false}
        onNavigateNext={onNavigateNext || (async () => {
          console.warn('onNavigateNext not provided to SharePointTab');
        })}
        onNavigatePrevious={onNavigatePrevious || (async () => {
          console.warn('onNavigatePrevious not provided to SharePointTab');
        })}
        onPageSizeChange={onPageSizeChange || (async () => {
          console.warn('onPageSizeChange not provided to SharePointTab');
        })}
        onSearch={undefined}
        appliedFilters={drillDownState.appliedFilters}
        onDocumentExclusion={onDocumentExclusion}
        onLibraryExclusion={onLibraryExclusion}
        isDocumentExcluded={isDocumentExcluded}
        sharepointId={sharepointId}
        sharepoint={sharepoint}
      />
    );
  }

  const totalPages = Math.ceil(filteredItems.length / ITEMS_PER_PAGE);
  const paginatedItems = filteredItems.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  const handleItemCheckboxChange = (itemId: string, type: string) => {
    handleCheckboxChange(itemId, key, sharepointId ?? 0, idField);
    const excludedKey = `excluded_${type}` as keyof ExcludedItems;
    updateExcludedItems(sharepointId, excludedKey, itemId);
  };

  const handleTitleClick = (item: any) => {
    if (onItemClick) {
      onItemClick(item, activeSubTab as 'lists' | 'document_libraries');
    }
  };

  // REMOVED: handleSearchChange function since search is now handled in SharePointToggleSwitch

  return (
    <div>
      {/* REMOVED: Search input since it's now in SharePointToggleSwitch */}
      
      {filteredItems.length === 0 ? (
        <h3 className="text-xl font-semibold mb-4">
          {searchTerm ? `No results found for "${searchTerm}"` : "No data found"}
        </h3>
      ) : (
        <>
          <table className="w-full border border-gray-300">
            <thead>
              <tr className="bg-gray-100">
                <th className="border text-left p-2 w-12">Select</th>
                <th className="border text-left p-2">Title</th>
                <th className="border text-left p-2 w-40">Created By</th>
                <th className="border text-left p-2 w-32">Created Date</th>
              </tr>
            </thead>
            <tbody>
              {paginatedItems.map((item) => {
                const itemId = item[idField] ?? "";
                const itemName = nameField.split('.').reduce((obj, key) => obj?.[key], item);

                return (
                  <tr key={itemId} className="border hover:bg-gray-50">
                    <td className="border text-center p-2">
                      <div className="flex justify-center">
                        {(() => {
                          console.log('🔍 Props check:', {
                            activeSubTab,
                            hasGetLibrarySelectionState: !!getLibrarySelectionState,
                            hasOnLibraryExclusion: !!onLibraryExclusion,
                            isDocumentLibraries: activeSubTab === 'document_libraries'
                          });
                          console.log('🔄 About to call renderLibraryCheckbox for:', itemId);
                          const result = renderLibraryCheckbox(item, itemId);
                          console.log('🔄 renderLibraryCheckbox returned:', result);
                          return result;
                        })()}
                      </div>
                    </td>
                    
                    <td className="border text-left p-2">
                      {onItemClick ? (
                        <button
                          className="text-left text-blue-600 hover:text-blue-800 hover:underline font-medium break-words w-full"
                          onClick={() => handleTitleClick(item)}
                          title="Click to view items"
                        >
                          {renderItemTitle(item, itemName)}
                        </button>
                      ) : (
                        <label className="text-gray break-words w-full cursor-pointer" htmlFor={`checkbox-${itemId}`}>
                          {renderItemTitle(item, itemName)}
                        </label>
                      )}
                    </td>
                    
                    <td className="border text-left p-2 text-sm">
                      <span className="text-gray-700">
                        {getCreatedBy(item)}
                      </span>
                    </td>
                    
                    <td className="border text-left p-2 text-sm">
                      <span className="text-gray-700">
                        {formatDate(item.created)}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          
          
        </>
      )}
    </div>
  );
};

export default SharePointTab;